import { SynergySystem } from './SynergySystem';
import Phaser from 'phaser';
import { ts } from '../utils/textStyle';
import { GAME_HEIGHT, GAME_WIDTH } from '../config/game';
import { RunState } from './runState';
import { SeededRandom } from './seededRandom';
import { PartnerSystem } from './PartnerSystem';
import { SHOP_CATALOG, PartnerType, ShopItemDefinition, getCatalogItem } from './shopCatalog';
import { RelicSystem } from './RelicSystem';
import { RELIC_RARITY_META, RelicDefinition } from './relicCatalog';
import { getTunables } from '../config/tunables';
import type { PartnerLevel } from './PartnerBehaviors';
import { formatBuildRoutes, getShopItemBuildRoutes } from './buildRoutes';

type ShopCard = {
  rollId: number;
  item: ShopItemDefinition | RelicDefinition;
  partnerLevel?: PartnerLevel;
  locked: boolean;
  purchased: boolean;
};

type OwnedSlot = {
  itemId: string;
  cost: number;
  level: PartnerLevel;
};

export interface ShopSystemModifiers {
  rerollDiscount: number;
}

export type ShopAutomationStrategy = 'balanced' | 'star' | 'relic' | 'synergy' | 'economy';

const MAX_PARTNER_SLOTS = 6;
const CARD_WIDTH = 150;
const CARD_HEIGHT = 118;

export class ShopSystem {
  private readonly rng: SeededRandom;
  private readonly cards: ShopCard[] = [];
  private readonly ownedSlots: OwnedSlot[] = [{ itemId: 'gunner', cost: 0, level: 1 }];
  private panel?: Phaser.GameObjects.Container;
  private rollCounter = 0;
  private currentWave = 1;
  private message = '';
  private selectedMergeSlot?: number;
  private pendingSaleSlot?: number;
  private usedFreeReroll = false;
  private consecutiveRerolls = 0;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly runState: RunState,
    private readonly partnerSystem: PartnerSystem,
    private readonly relicSystem: RelicSystem,
    seed: string,
    private readonly modifiers: ShopSystemModifiers = { rerollDiscount: 0 },
  ) {
    this.rng = new SeededRandom(`${seed}:shop`);
  }

  openForWave(wave: number, onContinue: () => void) {
    if (!Number.isInteger(wave) || wave < 1) {
      throw new Error(`Invalid shop wave: ${wave}`);
    }

    this.currentWave = wave;
    this.usedFreeReroll = false;
    this.consecutiveRerolls = 0;
    this.pendingSaleSlot = undefined;
    this.selectedMergeSlot = undefined;
    this.generateCards();
    this.applyFirstWavePartnerBudget();
    this.render(onContinue, true);
  }

  hide() {
    this.panel?.destroy(true);
    this.panel = undefined;
  }

  autoResolve(strategy: ShopAutomationStrategy, onContinue: () => void) {
    let purchased = 0;
    for (let attempt = 0; attempt < 8; attempt += 1) {
      const choice = this.pickAutomationCard(strategy);
      if (!choice) {
        break;
      }
      const before = this.runState.snapshot().materials;
      this.buy(choice, onContinue);
      const after = this.runState.snapshot().materials;
      if (after === before && !choice.purchased) {
        break;
      }
      purchased += 1;
    }
    this.message = purchased > 0 ? `自动购买 ${purchased} 项` : '自动跳过商店';
    onContinue();
    return { purchased };
  }

  private pickAutomationCard(strategy: ShopAutomationStrategy) {
    const affordable = this.cards.filter((card) => (
      !card.purchased &&
      this.getItemCost(card.item, card.item.kind === 'partner' ? this.getCardPartnerLevel(card) : undefined) <= this.runState.materials
    ));
    if (affordable.length === 0) {
      return undefined;
    }
    return affordable
      .map((card) => ({ card, score: this.scoreAutomationCard(card, strategy) }))
      .sort((a, b) => b.score - a.score)[0]?.card;
  }

  private scoreAutomationCard(card: ShopCard, strategy: ShopAutomationStrategy) {
    const partnerLevel = card.item.kind === 'partner' ? this.getCardPartnerLevel(card) : undefined;
    const cost = this.getItemCost(card.item, partnerLevel);
    if (card.item.kind === 'relic') {
      let score = RELIC_RARITY_META[card.item.rarity].weight * 0.4 + 5;
      if (strategy === 'relic') score += 7;
      if (strategy === 'economy' && card.item.tag === '经济') score += 6;
      if (strategy === 'star') score -= 1;
      return score - cost * 0.1;
    }

    let score = 4 + (partnerLevel ?? 1) * 2;
    const type = card.item.partnerType;
    const ownedTypes = this.ownedSlots.map((slot) => getCatalogItem(slot.itemId).partnerType);
    const sameLevelOwned = this.ownedSlots.some((slot) => (
      getCatalogItem(slot.itemId).partnerType === type &&
      slot.level === partnerLevel &&
      slot.level < 5
    ));
    if (sameLevelOwned) score += strategy === 'star' ? 12 : 6;
    if (strategy === 'synergy' && ownedTypes.some((ownedType) => (
      getShopItemBuildRoutes(card.item).some((route) => getShopItemBuildRoutes(getCatalogItem(this.ownedSlots.find((slot) => getCatalogItem(slot.itemId).partnerType === ownedType)?.itemId ?? 'gunner')).some((ownedRoute) => ownedRoute.id === route.id))
    ))) {
      score += 7;
    }
    if (strategy === 'balanced' && this.ownedSlots.length < MAX_PARTNER_SLOTS) score += 4;
    if (strategy === 'economy') score -= 1;
    return score - cost * 0.12;
  }

  private generateCards() {
    const nextCards: ShopCard[] = this.cards.filter((card) => card.locked && !card.purchased);

    const candidateLimit = this.getShopCandidateLimit();
    let partnerCount = nextCards.filter((card) => card.item.kind === 'partner').length;
    let relicCount = nextCards.filter((card) => card.item.kind === 'relic').length;

    while (nextCards.length < candidateLimit) {
      const shouldRollRelic = relicCount < 2 && (partnerCount >= 2 || this.rng.next() > 0.45);
      const item = shouldRollRelic ? this.pickRelicCandidate() : this.rng.pick(SHOP_CATALOG);
      if (item.kind === 'partner') {
        partnerCount += 1;
      } else {
        relicCount += 1;
      }
      this.rollCounter += 1;
      nextCards.push({
        rollId: this.rollCounter,
        item,
        partnerLevel: item.kind === 'partner' ? this.rollPartnerLevel() : undefined,
        locked: false,
        purchased: false,
      });
    }

    this.cards.splice(0, this.cards.length, ...nextCards.slice(0, candidateLimit));
  }

  private reroll(onContinue: () => void) {
    const cost = this.getRerollCost();
    const usesRelicFreeReroll = this.usesRelicFreeReroll();
    if (cost > 0 && !this.runState.spendMaterials(cost)) {
      this.message = `材料不足，刷新需要 ${cost}`;
      this.playErrorAnim();
      this.render(onContinue, false);
      return;
    }

    if (usesRelicFreeReroll) {
      this.usedFreeReroll = true;
    }
    if (cost > 0) {
      this.consecutiveRerolls += 1;
    } else {
      this.consecutiveRerolls = 0;
    }
    const snapshot = this.runState.snapshot();
    this.runState.recordRunPurchase({
      wave: snapshot.wave,
      kind: 'reroll',
      itemId: 'reroll',
      label: cost === 0 ? '免费刷新' : '刷新商店',
      cost,
      refund: 0,
      materialsAfter: snapshot.materials,
    });
    this.pendingSaleSlot = undefined;
    this.selectedMergeSlot = undefined;
    this.message = cost === 0 ? '已免费刷新' : `已刷新，消耗 ${cost}`;
    this.generateCards();
    this.render(onContinue, false);
  }

  private toggleLock(card: ShopCard, onContinue: () => void) {
    if (card.purchased) {
      return;
    }

    card.locked = !card.locked;
    this.message = card.locked ? '已锁定候选' : '已取消锁定';
    this.render(onContinue, false);
  }

  private buy(card: ShopCard, onContinue: () => void) {
    if (card.purchased) {
      return;
    }

    const partnerLevel = card.item.kind === 'partner' ? this.getCardPartnerLevel(card) : undefined;
    const mergeLevel = card.item.kind === 'partner' && this.ownedSlots.length >= MAX_PARTNER_SLOTS
      ? this.findAutoMergeLevel(card.item.partnerType, partnerLevel)
      : undefined;
    if (card.item.kind === 'partner' && this.ownedSlots.length >= MAX_PARTNER_SLOTS && mergeLevel === undefined) {
      this.message = '伙伴槽已满，需要同名同级才能合成';
      this.playErrorAnim();
      this.render(onContinue, false);
      return;
    }

    const cost = this.getItemCost(card.item, partnerLevel);
    if (!this.runState.spendMaterials(cost)) {
      this.message = `材料不足，还差 ${cost - this.runState.materials}`;
      this.playErrorAnim();
      this.render(onContinue, false);
      return;
    }

    if (card.item.kind === 'partner') {
      const level = partnerLevel ?? 1;
      this.ownedSlots.push({ itemId: card.item.id, cost, level });
      this.partnerSystem.addPartner(card.item.partnerType, level);
      if (mergeLevel !== undefined) {
        const result = this.mergeFirstPair(card.item.partnerType, mergeLevel);
        this.message = result ? `${card.item.name} 合成 Lv.${result.level}` : `招募 ${card.item.name}`;
      } else {
        this.message = `招募 ${card.item.name} Lv.${level}`;
      }
    } else {
      try {
        this.relicSystem.buyRelic(card.item.id);
        this.message = `获得 ${card.item.name}`;
      } catch (error) {
        this.runState.addMaterials(cost);
        this.message = error instanceof Error ? error.message : '遗物购买失败';
        this.playErrorAnim();
        this.render(onContinue, false);
        return;
      }
    }
    const snapshot = this.runState.snapshot();
    this.runState.recordRunPurchase({
      wave: snapshot.wave,
      kind: card.item.kind === 'partner' ? 'buy-partner' : 'buy-relic',
      itemId: card.item.id,
      label: card.item.name,
      cost,
      refund: 0,
      level: card.item.kind === 'partner' ? partnerLevel : undefined,
      materialsAfter: snapshot.materials,
      note: card.item.kind === 'relic' ? card.item.tag : card.item.tag,
    });
    card.purchased = true;
    card.locked = false;
    this.consecutiveRerolls = 0;
    this.pendingSaleSlot = undefined;
    this.selectedMergeSlot = undefined;
    
    // Play success flash
    this.scene.cameras.main.flash(150, 255, 255, 255);
    
    this.render(onContinue, false);
  }

  private sellSlot(slotIndex: number, onContinue: () => void) {
    if (!Number.isInteger(slotIndex) || slotIndex < 0 || slotIndex >= this.ownedSlots.length) {
      throw new Error(`Invalid shop slot index: ${slotIndex}`);
    }

    const refund = this.getSlotRefund(slotIndex);
    const [slot] = this.ownedSlots.splice(slotIndex, 1);
    const item = getCatalogItem(slot.itemId);
    this.runState.addMaterials(refund);
    this.partnerSystem.removePartnerAt(slotIndex);
    const snapshot = this.runState.snapshot();
    this.runState.recordRunPurchase({
      wave: snapshot.wave,
      kind: 'sell-partner',
      itemId: slot.itemId,
      label: item.name,
      cost: 0,
      refund,
      level: slot.level,
      materialsAfter: snapshot.materials,
    });
    this.message = `出售 ${item.name}，返还 ${refund}`;
    this.pendingSaleSlot = undefined;
    this.selectedMergeSlot = undefined;
    
    // Play sell effect
    this.scene.cameras.main.shake(100, 0.005);
    
    this.render(onContinue, false);
  }

  private mergeSlots(firstIndex: number, secondIndex: number) {
    const keepIndex = Math.min(firstIndex, secondIndex);
    const removeIndex = Math.max(firstIndex, secondIndex);
    const result = this.partnerSystem.mergeSlots(firstIndex, secondIndex);
    const item = getCatalogItem(this.ownedSlots[keepIndex].itemId);
    this.ownedSlots[keepIndex].level = result.level;
    this.ownedSlots[keepIndex].cost += this.ownedSlots[removeIndex].cost;
    this.ownedSlots.splice(removeIndex, 1);
    const snapshot = this.runState.snapshot();
    this.runState.recordRunPurchase({
      wave: snapshot.wave,
      kind: 'merge-partner',
      itemId: item.id,
      label: item.name,
      cost: 0,
      refund: 0,
      level: result.level,
      materialsAfter: snapshot.materials,
      note: `Lv.${result.level - 1} + Lv.${result.level - 1}`,
    });
    this.selectedMergeSlot = undefined;
    return result;
  }

  private mergeFirstPair(type: PartnerType, level: PartnerLevel) {
    const firstIndex = this.ownedSlots.findIndex((slot) => getCatalogItem(slot.itemId).partnerType === type && slot.level === level);
    if (firstIndex < 0) {
      return undefined;
    }
    const secondIndex = this.ownedSlots.findIndex((slot, index) => (
      index !== firstIndex && getCatalogItem(slot.itemId).partnerType === type && slot.level === level
    ));
    if (secondIndex < 0) {
      return undefined;
    }
    return this.mergeSlots(firstIndex, secondIndex);
  }

  private findAutoMergeLevel(type: PartnerType, level?: PartnerLevel) {
    if (level === undefined || level >= 5) {
      return undefined;
    }
    for (const slot of this.ownedSlots) {
      if (getCatalogItem(slot.itemId).partnerType === type && slot.level === level) {
        return slot.level;
      }
    }
    return undefined;
  }

  private canMergeOwnedSlots(firstIndex: number, secondIndex: number) {
    if (firstIndex === secondIndex) {
      return false;
    }
    const first = this.ownedSlots[firstIndex];
    const second = this.ownedSlots[secondIndex];
    if (!first || !second || first.level !== second.level || first.level >= 5) {
      return false;
    }
    return getCatalogItem(first.itemId).partnerType === getCatalogItem(second.itemId).partnerType;
  }

  private getMergePartnerName(slotIndex: number) {
    const slot = this.ownedSlots[slotIndex];
    return slot ? getCatalogItem(slot.itemId).name : '';
  }

  private playErrorAnim() {
    this.scene.cameras.main.shake(100, 0.005);
  }

  private getRerollCost() {
    if (this.isShopCleared() || this.usesRelicFreeReroll()) {
      return 0;
    }
    const pricing = getTunables().pricing;
    const baseCost = pricing.rerollBaseCost + Math.floor((this.currentWave - 1) / 2) * pricing.rerollCostPerTwoWaves;
    const streakMultiplier = this.getRerollStreakMultiplier();
    return Math.max(
      0,
      Math.ceil((baseCost - this.modifiers.rerollDiscount) * streakMultiplier * getTunables().economy.rerollCostMultiplier),
    );
  }

  private isShopCleared() {
    return this.cards.length > 0 && this.cards.every((card) => card.purchased);
  }

  private usesRelicFreeReroll() {
    return !this.isShopCleared()
      && this.relicSystem.getModifiers(this.getRelicContext()).hasFreeReroll
      && !this.usedFreeReroll;
  }

  private getRerollStreakMultiplier() {
    const pricing = getTunables().pricing;
    if (this.consecutiveRerolls <= 0) {
      return 1;
    }
    if (this.consecutiveRerolls === 1) {
      return pricing.rerollSecondCostMultiplier;
    }
    if (this.consecutiveRerolls === 2) {
      return pricing.rerollThirdCostMultiplier;
    }
    return pricing.rerollMaxCostMultiplier;
  }

  private getShopCandidateLimit() {
    return this.relicSystem.getModifiers(this.getRelicContext()).shopCandidateLimit;
  }

  private getItemCost(item: ShopItemDefinition | RelicDefinition, partnerLevel?: PartnerLevel) {
    const discount = this.relicSystem.getModifiers(this.getRelicContext()).shopDiscount;
    const kindMultiplier = item.kind === 'partner'
      ? getTunables().pricing.partnerPriceMultiplier
      : getTunables().pricing.relicPriceMultiplier;
    const levelMultiplier = item.kind === 'partner' ? this.getPartnerLevelCostMultiplier(partnerLevel ?? 1) : 1;
    return Math.max(1, Math.ceil(item.cost * levelMultiplier * kindMultiplier * getTunables().economy.shopPriceMultiplier * (1 - discount)));
  }

  private getPartnerLevelCostMultiplier(level: PartnerLevel) {
    const pricing = getTunables().pricing;
    switch (level) {
      case 1:
        return 1;
      case 2:
        return pricing.partnerLevel2PriceMultiplier;
      case 3:
        return pricing.partnerLevel3PriceMultiplier;
      case 4:
        return pricing.partnerLevel4PriceMultiplier;
      case 5:
        return pricing.partnerLevel5PriceMultiplier;
      default:
        throw new Error(`Invalid partner level cost: ${level}`);
    }
  }

  private applyFirstWavePartnerBudget() {
    if (this.currentWave !== 1) {
      return;
    }
    const minVisiblePartnerCost = this.cards
      .filter((card) => card.item.kind === 'partner')
      .map((card) => this.getItemCost(card.item, this.getCardPartnerLevel(card)))
      .reduce((lowest, cost) => Math.min(lowest, cost), Infinity);
    const fallbackPartnerCost = SHOP_CATALOG
      .map((item) => this.getItemCost(item, 1))
      .reduce((lowest, cost) => Math.min(lowest, cost), Infinity);
    const requiredBudget = Math.ceil(Math.max(
      getTunables().pricing.firstWaveMinPartnerBudget,
      Number.isFinite(minVisiblePartnerCost) ? minVisiblePartnerCost : fallbackPartnerCost,
    ));
    if (requiredBudget <= 0 || this.runState.materials >= requiredBudget) {
      return;
    }
    const grant = requiredBudget - this.runState.materials;
    this.runState.addMaterials(grant);
    const snapshot = this.runState.snapshot();
    this.runState.recordRunPurchase({
      wave: snapshot.wave,
      kind: 'economy-grant',
      itemId: 'first-wave-budget',
      label: '首波构筑补给',
      cost: 0,
      refund: grant,
      materialsAfter: snapshot.materials,
    });
    this.message = `首波构筑补给 +${grant} 材料`;
  }

  private rollPartnerLevel(): PartnerLevel {
    const roll = this.rng.next();
    if (this.currentWave >= 9) {
      if (roll < 0.18) {
        return 3;
      }
      if (roll < 0.58) {
        return 2;
      }
      return 1;
    }
    if (this.currentWave >= 7) {
      if (roll < 0.1) {
        return 3;
      }
      if (roll < 0.45) {
        return 2;
      }
      return 1;
    }
    if (this.currentWave >= 4) {
      return roll < 0.25 ? 2 : 1;
    }
    return 1;
  }

  private getCardPartnerLevel(card: ShopCard): PartnerLevel {
    if (card.item.kind !== 'partner') {
      throw new Error(`Shop card is not partner: ${card.item.id}`);
    }
    return card.partnerLevel ?? 1;
  }

  private getSlotRefund(slotIndex: number) {
    const slot = this.ownedSlots[slotIndex];
    if (!slot) {
      throw new Error(`Invalid refund slot index: ${slotIndex}`);
    }
    return Math.floor(slot.cost * this.relicSystem.getModifiers(this.getRelicContext()).sellRefundRate);
  }

  private pickRelicCandidate() {
    const available = this.relicSystem.getAvailableRelics().filter((relic) => this.isRelicUnlockedForWave(relic));
    if (available.length <= 0) {
      const fallback = this.relicSystem.getAvailableRelics();
      return fallback.length > 0 ? this.rng.pick(fallback) : this.rng.pick(SHOP_CATALOG);
    }
    const totalWeight = available.reduce((total, relic) => total + RELIC_RARITY_META[relic.rarity].weight, 0);
    let roll = this.rng.next() * totalWeight;
    for (const relic of available) {
      roll -= RELIC_RARITY_META[relic.rarity].weight;
      if (roll <= 0) {
        return relic;
      }
    }
    return available[available.length - 1];
  }

  private isRelicUnlockedForWave(relic: RelicDefinition) {
    if (relic.rarity === 'legendary' && this.currentWave < 7) {
      return false;
    }
    if (relic.rarity === 'epic' && this.currentWave < 4) {
      return false;
    }
    return true;
  }

  private getRelicContext() {
    const snapshot = this.runState.snapshot();
    return {
      wave: snapshot.wave,
      materials: snapshot.materials,
      hp: snapshot.hp,
      maxHp: snapshot.maxHp,
      partnerTypes: this.partnerSystem.getPartnerTypes(),
    };
  }

  private render(onContinue: () => void, isFirstRender: boolean = false) {
    this.hide();

    const snapshot = this.runState.snapshot();
    const panel = this.scene.add.container(0, 0);
    panel.setDepth(220);

    const shade = this.scene.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x080705, 0.72);
    const background = this.scene.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, 356, 700, 0x201a14, 0.96);
    background.setStrokeStyle(2, 0xe0b15a, 0.9);

    const title = this.scene.add.text(GAME_WIDTH / 2, 92, `波次 ${this.currentWave} 结束`, ts({
      fontFamily: 'Arial, sans-serif',
      fontSize: '24px',
      color: '#f5e5c8',
      fontStyle: 'bold',
    })).setOrigin(0.5);

    const walletBg = this.scene.add.rectangle(GAME_WIDTH / 2, 124, 200, 28, 0x110d0a, 0.8);
    walletBg.setStrokeStyle(1, 0x4a3f31);

    const wallet = this.scene.add.text(GAME_WIDTH / 2, 124, `💎 ${snapshot.materials}   ⭐ Lv.${snapshot.level}`, ts({
      fontFamily: 'Arial, sans-serif',
      fontSize: '15px',
      color: '#9ee7a8',
      fontStyle: 'bold',
    })).setOrigin(0.5);

    const slotLabel = this.scene.add.text(28, 152, '伙伴队伍 (点两个同名同级合成)', ts({
      fontFamily: 'Arial, sans-serif',
      fontSize: '13px',
      color: '#e0b15a',
      fontStyle: 'bold'
    }));

    const children: Phaser.GameObjects.GameObject[] = [shade, background, title, walletBg, wallet, slotLabel];
    
    // Render synergy HUD
    children.push(...this.renderSynergyHUD());
    children.push(...this.renderRelicStacks());

    children.push(...this.renderSlots(onContinue));
    children.push(...this.renderCards(onContinue));

    const rerollButton = this.createButton(104, 716, 142, 48, `刷新 💎${this.getRerollCost()}`, 0x514435, () => {
      this.reroll(onContinue);
    });
    const nextButton = this.createButton(284, 716, 142, 48, '下一波 ➔', 0xe0b15a, onContinue);
    children.push(...rerollButton, ...nextButton);

    if (this.message) {
      const msgBg = this.scene.add.rectangle(GAME_WIDTH / 2, 666, 280, 24, 0x110d0a, 0.8);
      const messageText = this.scene.add.text(GAME_WIDTH / 2, 666, this.message, ts({
        fontFamily: 'Arial, sans-serif',
        fontSize: '13px',
        color: '#f7d98a',
      })).setOrigin(0.5);
      children.push(msgBg, messageText);
    }

    if (this.pendingSaleSlot !== undefined) {
      children.push(...this.renderSellConfirmation(this.pendingSaleSlot, onContinue));
    }

    panel.add(children);
    this.panel = panel;

    if (isFirstRender) {
      panel.setAlpha(0);
      panel.setScale(0.95);
      this.scene.tweens.add({
        targets: panel,
        alpha: 1,
        scale: 1,
        duration: 250,
        ease: 'Back.easeOut'
      });
    }
  }

  private renderSynergyHUD() {
    const children: Phaser.GameObjects.GameObject[] = [];
    const synergy = this.partnerSystem.getSynergyState();
    const synText = synergy.snapshots
      .map((snapshot) => `${snapshot.tag}${snapshot.count}${snapshot.tiers.length > 0 ? '*' : ''}`)
      .join('  ');

    const synergyLabel = this.scene.add.text(28, 242, synText, ts({
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      color: '#e0b15a',
    }));
    children.push(synergyLabel);
    
    // Draw a divider line
    const divider = this.scene.add.rectangle(GAME_WIDTH / 2, 268, 310, 1, 0x4a3f31);
    children.push(divider);

    return children;
  }

  private renderRelicStacks() {
    const children: Phaser.GameObjects.GameObject[] = [];
    const stacks = this.relicSystem.getOwnedStacks();
    const totalStacks = stacks.reduce((total, stack) => total + stack.count, 0);
    const stackText = stacks.length === 0
      ? '遗物 无'
      : `遗物 ${totalStacks}层 ${stacks
        .slice(0, 4)
        .map((stack) => `${stack.definition.name}x${stack.count}`)
        .join('  ')}${stacks.length > 4 ? `  +${stacks.length - 4}` : ''}`;

    const relicLine = this.scene.add.text(28, 256, stackText, ts({
      fontFamily: 'Arial, sans-serif',
      fontSize: '11px',
      color: '#c7b18a',
      wordWrap: { width: 332 },
    }));
    children.push(relicLine);

    return children;
  }

  private renderSlots(onContinue: () => void) {
    const children: Phaser.GameObjects.GameObject[] = [];
    const startX = 49;
    const startY = 196;
    const gap = 58;

    for (let index = 0; index < MAX_PARTNER_SLOTS; index += 1) {
      const slot = this.ownedSlots[index];
      const item = slot ? getCatalogItem(slot.itemId) : undefined;
      const x = startX + index * gap;
      const selected = this.selectedMergeSlot === index;
      const canMergeWithSelected = this.selectedMergeSlot !== undefined && this.canMergeOwnedSlots(this.selectedMergeSlot, index);
      const hasMergePair = slot ? this.ownedSlots.some((_, otherIndex) => this.canMergeOwnedSlots(index, otherIndex)) : false;
      
      const box = this.scene.add.rectangle(x, startY, 48, 48, item?.color ?? 0x110d0a, 1);
      box.setStrokeStyle(
        selected || canMergeWithSelected ? 3 : 2,
        selected ? 0xfff0a8 : canMergeWithSelected ? 0x9effa6 : item ? 0xf4d48a : 0x302820,
        1,
      );

      const label = this.scene.add.text(x, startY, item ? item.name.slice(0, 1) : '空', ts({
        fontFamily: 'Arial, sans-serif',
        fontSize: item ? '20px' : '12px',
        color: item ? '#18110a' : '#4a3f31',
        fontStyle: 'bold',
      })).setOrigin(0.5);

      children.push(box, label);

      if (slot) {
        const level = this.scene.add.text(x, startY + 29, `★${slot.level}`, ts({
          fontFamily: 'Arial, sans-serif',
          fontSize: '11px',
          color: slot.level >= 5 ? '#fff0a8' : '#f5dfad',
          fontStyle: 'bold',
        })).setOrigin(0.5);
        children.push(level);

        if (hasMergePair) {
          const badge = this.scene.add.text(x, startY - 30, '合', ts({
            fontFamily: 'Arial, sans-serif',
            fontSize: '10px',
            color: '#15110b',
            backgroundColor: '#9effa6',
            fontStyle: 'bold',
            padding: { left: 3, right: 3, top: 1, bottom: 1 },
          })).setOrigin(0.5);
          children.push(badge);
        }

        // Info button for tooltip
        const infoBtn = this.scene.add.text(x + 22, startY - 22, 'ℹ', ts({
          fontFamily: 'Arial, sans-serif',
          fontSize: '13px',
          color: '#8a7a6a',
          fontStyle: 'bold',
        })).setOrigin(0.5).setInteractive({ useHandCursor: true });
        infoBtn.on('pointerover', () => infoBtn.setColor('#ffffff'));
        infoBtn.on('pointerout', () => infoBtn.setColor('#8a7a6a'));
        infoBtn.on('pointerdown', (_pointer: Phaser.Input.Pointer, _gx: number, _gy: number, ev: Phaser.Types.Input.EventData) => {
          ev.stopPropagation();
          window.dispatchEvent(new CustomEvent('show-item-tooltip', {
            detail: { type: 'partner', item, level: slot.level, synergies: new SynergySystem().calculate(this.ownedSlots.map(s => (getCatalogItem(s.itemId) as any).partnerType).filter(Boolean)).snapshots ?? [] },
          }));
        });
        children.push(infoBtn);

        box.setInteractive({ useHandCursor: true });
        label.setInteractive({ useHandCursor: true });

        box.on('pointerover', () => box.setStrokeStyle(3, 0xffffff, 1));
        box.on('pointerout', () => box.setStrokeStyle(2, 0xf4d48a, 1));

        const handler = () => {
          if (this.selectedMergeSlot !== undefined) {
            if (this.canMergeOwnedSlots(this.selectedMergeSlot, index)) {
              const result = this.mergeSlots(this.selectedMergeSlot, index);
              this.message = `${this.getMergePartnerName(result.index)} 合成 Lv.${result.level}`;
              this.scene.cameras.main.flash(150, 255, 240, 160);
            } else {
              this.selectedMergeSlot = undefined;
              this.message = '只能合成同名同级伙伴';
              this.playErrorAnim();
            }
            this.render(onContinue, false);
            return;
          }
          if (hasMergePair) {
            this.selectedMergeSlot = index;
            this.pendingSaleSlot = undefined;
            this.message = `选择另一个 Lv.${slot.level} ${this.getMergePartnerName(index)} 合成`;
            this.render(onContinue, false);
            return;
          }
          this.selectedMergeSlot = undefined;
          this.pendingSaleSlot = index;
          this.message = '';
          this.render(onContinue, false);
        };
        box.on('pointerdown', handler);
        label.on('pointerdown', handler);
      }
    }

    return children;
  }

  private renderCards(onContinue: () => void) {
    const children: Phaser.GameObjects.GameObject[] = [];

    this.cards.forEach((card, index) => {
      const col = index % 2;
      const row = Math.floor(index / 2);
      const x = 111 + col * 168;
      const y = 352 + row * 148;

      const relicItem = card.item.kind === 'relic' ? (card.item as RelicDefinition) : null;
      const rarityColor = relicItem ? RELIC_RARITY_META[relicItem.rarity].color : 0x4a3f31;
      
      // Glow effect for high rarity
      let glow: Phaser.GameObjects.Rectangle | null = null;
      if (!card.purchased && relicItem) {
        const isHighRarity = relicItem.rarity === 'epic' || relicItem.rarity === 'legendary';
        if (isHighRarity) {
          glow = this.scene.add.rectangle(x, y, CARD_WIDTH + 10, CARD_HEIGHT + 10, rarityColor, 0.3);
          this.scene.tweens.add({
            targets: glow,
            alpha: 0.6,
            duration: 800,
            yoyo: true,
            repeat: -1
          });
        }
      }

      const baseColor = card.purchased ? 0x241d14 : card.item.color;
      const cardBox = this.scene.add.rectangle(x, y, CARD_WIDTH, CARD_HEIGHT, baseColor, 1);
      cardBox.setStrokeStyle(card.locked ? 3 : 2, card.locked ? 0xf3e28e : (card.purchased ? 0x302820 : rarityColor), 1);
      
      // Inner background for text area
      const innerBg = this.scene.add.rectangle(x, y + 16, CARD_WIDTH - 8, CARD_HEIGHT - 40, 0x110d0a, 0.6);

      const partnerLevel = card.item.kind === 'partner' ? this.getCardPartnerLevel(card) : undefined;
      const displayName = card.item.kind === 'partner' && (partnerLevel ?? 1) > 1 ? `${card.item.name} Lv.${partnerLevel}` : card.item.name;
      const name = this.scene.add.text(x - 64, y - 46, displayName, ts({
        fontFamily: 'Arial, sans-serif',
        fontSize: '16px',
        color: card.purchased ? '#4a3f31' : '#f5e5c8',
        fontStyle: 'bold',
      }));

      const mergeLevel = card.item.kind === 'partner' ? this.findAutoMergeLevel(card.item.partnerType, partnerLevel) : undefined;
      const cardCost = this.getItemCost(card.item, partnerLevel);
      const buildRoutes = getShopItemBuildRoutes(card.item);
      const routeText = formatBuildRoutes(buildRoutes);
      const tagText = card.item.kind === 'partner' && this.ownedSlots.length >= MAX_PARTNER_SLOTS && mergeLevel !== undefined
        ? `${routeText} | 合成 Lv.${mergeLevel + 1} | 💎${cardCost}`
        : `${card.item.kind === 'relic' ? `遗物·${RELIC_RARITY_META[card.item.rarity].label}` : `${routeText} | Lv.${partnerLevel}`} | 💎${cardCost}`;
      const tag = this.scene.add.text(x - 64, y - 24, tagText, ts({
        fontFamily: 'Arial, sans-serif',
        fontSize: '12px',
        color: card.purchased ? '#302820' : buildRoutes[0].cssColor,
        fontStyle: 'bold',
      }));

      const descriptionText = card.purchased
        ? (card.item.kind === 'relic' ? '已获得' : '已招募')
        : card.item.kind === 'partner' && this.ownedSlots.length >= MAX_PARTNER_SLOTS
          ? (mergeLevel !== undefined ? `槽满，购买后同级二合一到 Lv.${mergeLevel + 1}` : `槽满，暂无 Lv.${partnerLevel} 同名伙伴可合成`)
          : card.item.description;
      const description = this.scene.add.text(x - 64, y + 2, descriptionText, ts({
        fontFamily: 'Arial, sans-serif',
        fontSize: '12px',
        color: card.purchased ? '#302820' : '#a9906a',
        wordWrap: { width: 130 },
        lineSpacing: 4
      }));

      const lock = this.scene.add.rectangle(x + 54, y - 44, 30, 24, card.locked ? 0x241d14 : 0xe0b15a, 1);
      lock.setStrokeStyle(1, 0x4a3f31, 1);
      const lockText = this.scene.add.text(x + 54, y - 44, card.locked ? '🔒' : '锁', ts({
        fontFamily: 'Arial, sans-serif',
        fontSize: '12px',
        color: card.locked ? '#f8df93' : '#24180a',
        fontStyle: 'bold',
      })).setOrigin(0.5);
      
            if (glow) children.push(glow);
      children.push(cardBox, innerBg, name, tag, description, lock, lockText);

      // Info button on shop cards (unpurchased only)
      if (!card.purchased) {
        const cardInfoBtn = this.scene.add.text(x + 54, y + 38, 'ℹ', ts({
          fontFamily: 'Arial, sans-serif',
          fontSize: '14px',
          color: '#8a7a6a',
          fontStyle: 'bold',
        })).setOrigin(0.5).setInteractive({ useHandCursor: true });
        cardInfoBtn.on('pointerover', () => cardInfoBtn.setColor('#ffffff'));
        cardInfoBtn.on('pointerout', () => cardInfoBtn.setColor('#8a7a6a'));
        cardInfoBtn.on('pointerdown', (_p: Phaser.Input.Pointer, _gx: number, _gy: number, ev: Phaser.Types.Input.EventData) => {
          ev.stopPropagation();
          window.dispatchEvent(new CustomEvent('show-item-tooltip', {
            detail: {
              type: card.item.kind,
              item: card.item,
              level: card.item.kind === 'partner' ? (partnerLevel ?? 1) : undefined,
              stacks: card.item.kind === 'relic' ? (this.relicSystem.getOwnedStacks().find(s => s.id === (card.item as RelicDefinition).id)?.count ?? 0) : undefined,
              synergies: [],
            },
          }));
        });
        children.push(cardInfoBtn);
      }

      if (!card.purchased) {
        cardBox.setInteractive({ useHandCursor: true });
        name.setInteractive({ useHandCursor: true });
        tag.setInteractive({ useHandCursor: true });
        description.setInteractive({ useHandCursor: true });
        lock.setInteractive({ useHandCursor: true });
        lockText.setInteractive({ useHandCursor: true });

        // Hover effects
        cardBox.on('pointerover', () => {
            if (!card.locked) cardBox.setStrokeStyle(2, 0xe0b15a, 1);
        });
        cardBox.on('pointerout', () => {
            if (!card.locked) cardBox.setStrokeStyle(2, 0x4a3f31, 1);
        });

        const buyHandler = () => {
            this.buy(card, onContinue);
        };
        cardBox.on('pointerdown', buyHandler);
        name.on('pointerdown', buyHandler);
        tag.on('pointerdown', buyHandler);
        description.on('pointerdown', buyHandler);

        const lockHandler = () => {
            this.toggleLock(card, onContinue);
        };
        lock.on('pointerdown', lockHandler);
        lockText.on('pointerdown', lockHandler);
      }
    });

    return children;
  }

  private renderSellConfirmation(slotIndex: number, onContinue: () => void) {
    const slot = this.ownedSlots[slotIndex];
    if (!slot) {
      this.pendingSaleSlot = undefined;
      return [];
    }

    const item = getCatalogItem(slot.itemId);
    const refund = this.getSlotRefund(slotIndex);
    const children: Phaser.GameObjects.GameObject[] = [];
    const blocker = this.scene.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x050302, 0.64);
    blocker.setInteractive({ useHandCursor: false });
    blocker.on('pointerdown', () => {
      this.pendingSaleSlot = undefined;
      this.message = '已取消出售';
      this.render(onContinue, false);
    });

    const box = this.scene.add.rectangle(GAME_WIDTH / 2, 420, 320, 200, 0x1f0b09, 0.98);
    box.setStrokeStyle(4, 0xff3333, 1);
    box.setInteractive({ useHandCursor: false });
    
    this.scene.tweens.add({
      targets: box,
      alpha: 0.85,
      yoyo: true,
      repeat: -1,
      duration: 600
    });

    const title = this.scene.add.text(GAME_WIDTH / 2, 350, '⚠️ 永久出售警告', ts({
      fontFamily: 'Arial, sans-serif',
      fontSize: '22px',
      color: '#ff4444',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4
    })).setOrigin(0.5);

    const detail = this.scene.add.text(GAME_WIDTH / 2, 410, `即将永久解雇 [${item.name} Lv.${slot.level}]\n\n换取 💎 ${refund} 结算材料`, ts({
      fontFamily: 'Arial, sans-serif',
      fontSize: '16px',
      color: '#ffccaa',
      align: 'center',
      lineSpacing: 6,
    })).setOrigin(0.5);

    const cancel = this.createButton(126, 486, 118, 42, '取消', 0x514435, () => {
      this.pendingSaleSlot = undefined;
      this.message = '已取消出售';
      this.render(onContinue, false);
    });
    const confirm = this.createButton(264, 486, 118, 42, '确认出售', 0xc94b3f, () => {
      if (this.pendingSaleSlot !== slotIndex) {
        this.pendingSaleSlot = undefined;
        this.message = '出售状态已失效';
        this.playErrorAnim();
        this.render(onContinue, false);
        return;
      }
      this.sellSlot(slotIndex, onContinue);
    });

    children.push(blocker, box, title, detail, ...cancel, ...confirm);
    return children;
  }

  private createButton(
    x: number,
    y: number,
    width: number,
    height: number,
    label: string,
    color: number,
    action: () => void,
  ) {
    const button = this.scene.add.rectangle(x, y, width, height, color, 1);
    button.setStrokeStyle(2, color === 0xe0b15a ? 0xffffff : 0x8a7a63, 0.8);
    button.setInteractive({ useHandCursor: true });
    const text = this.scene.add.text(x, y, label, ts({
      fontFamily: 'Arial, sans-serif',
      fontSize: '16px',
      color: color === 0xe0b15a ? '#24180a' : '#f5dfad',
      fontStyle: 'bold',
    })).setOrigin(0.5);
    
    button.on('pointerover', () => button.setAlpha(0.8));
    button.on('pointerout', () => button.setAlpha(1));
    
    const clickHandler = () => {
        action();
    };
    
    button.on('pointerdown', clickHandler);
    text.setInteractive({ useHandCursor: true }).on('pointerdown', clickHandler);
    
    return [button, text];
  }
}
